import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { CustomTaskFormModal } from '../components/tasks/CustomTaskFormModal';
import { TaskToggleRow } from '../components/tasks/TaskToggleRow';
import type { CharacterDto } from '../lib/roster';
import { useAccounts } from '../lib/roster';
import type { TaskDto } from '../lib/reference';
import { useReferenceTasks } from '../lib/reference';
import {
  useAttachTask,
  useCustomTasks,
  useDeleteCustomTask,
  useTaskAttachments,
  useUpdateAttachment,
  type CustomTaskDto,
  type TaskAttachmentDto,
} from '../lib/tasks';

const CADENCE_LABEL: Record<string, string> = { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly' };

function eligibilityReason(task: TaskDto, character: CharacterDto): string | null {
  if (task.levelReq != null && character.level < task.levelReq) {
    return `Requires level ${task.levelReq} — ${character.name} is level ${character.level}`;
  }
  return null;
}

interface OwnerRef {
  characterId?: number;
  accountId?: number;
}

interface TaskRef {
  taskDefSlug?: string;
  customTaskId?: number;
}

function findAttachment(
  attachments: TaskAttachmentDto[] | undefined,
  ref: TaskRef,
  owner: OwnerRef,
): TaskAttachmentDto | undefined {
  return attachments?.find((a) => {
    const sameTask = ref.taskDefSlug ? a.taskDefSlug === ref.taskDefSlug : a.customTaskId === ref.customTaskId;
    const sameOwner = owner.characterId ? a.characterId === owner.characterId : a.accountId === owner.accountId;
    return sameTask && sameOwner;
  });
}

export function TaskSetup() {
  const { data: accounts, isPending: accountsPending, isError: accountsError } = useAccounts();
  const { data: catalogTasks } = useReferenceTasks();
  const { data: customTasks } = useCustomTasks();
  const { data: attachments } = useTaskAttachments();
  const attachTask = useAttachTask();
  const updateAttachment = useUpdateAttachment();
  const deleteCustomTask = useDeleteCustomTask();

  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [creatingCustomTask, setCreatingCustomTask] = useState(false);
  const [deletingCustomTask, setDeletingCustomTask] = useState<CustomTaskDto | null>(null);

  if (accountsPending) {
    return <p className="text-sm text-text-muted">Loading task setup…</p>;
  }
  if (accountsError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load task setup. Please retry in a moment.
      </p>
    );
  }
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main and a few characters, then come back to choose what to track."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const account = accounts.find((a) => a.id === selectedAccountId) ?? accounts[0]!;

  const toggle = (ref: TaskRef, owner: OwnerRef, existing: TaskAttachmentDto | undefined) => {
    if (existing) {
      updateAttachment.mutate({ attachmentId: existing.id, enabled: !existing.enabled });
    } else {
      attachTask.mutate({ ...ref, ...owner, enabled: true });
    }
  };

  const accountScopedCatalog = (catalogTasks ?? []).filter((t) => t.scope !== 'CHARACTER');
  const accountScopedCustom = (customTasks ?? []).filter((t) => t.scope !== 'CHARACTER');
  const characterScopedCatalog = (catalogTasks ?? []).filter((t) => t.scope === 'CHARACTER');
  const characterScopedCustom = (customTasks ?? []).filter((t) => t.scope === 'CHARACTER');

  const togglePending = attachTask.isPending || updateAttachment.isPending;

  return (
    <div className="stagger-children space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-xl font-semibold text-text">Manage tasks</h1>
          <p className="mt-1 max-w-prose text-sm text-text-muted">
            Turn on the dailies, weeklies and monthlies you want to track, per account or per
            character. Greyed-out tasks are below a character's level requirement — you can still
            enable them.
          </p>
        </div>
        <Link
          to="/checklist"
          className="min-h-tap rounded-lg border border-border-strong px-3 py-2 text-sm font-semibold text-text-muted hover:border-accent hover:text-accent"
        >
          Back to checklist
        </Link>
      </div>

      {accounts.length > 1 && (
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Account">
          {accounts.map((a) => (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={a.id === account.id}
              onClick={() => setSelectedAccountId(a.id)}
              className={[
                'min-h-tap rounded-full px-4 text-sm font-semibold transition-colors',
                a.id === account.id
                  ? 'bg-accent text-accent-contrast'
                  : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
              ].join(' ')}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      <section className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
        <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
          Account &amp; world tasks — {account.label}
        </h2>
        <ul className="space-y-2">
          {accountScopedCatalog.map((task) => {
            const existing = findAttachment(attachments, { taskDefSlug: task.slug }, { accountId: account.id });
            return (
              <TaskToggleRow
                key={task.slug}
                name={task.name}
                meta={`${CADENCE_LABEL[task.cadence]} · max ${task.maxCount}`}
                verified={task.verified}
                checked={existing?.enabled ?? false}
                pending={togglePending}
                onToggle={() => toggle({ taskDefSlug: task.slug }, { accountId: account.id }, existing)}
              />
            );
          })}
          {accountScopedCustom.map((task) => {
            const existing = findAttachment(attachments, { customTaskId: task.id }, { accountId: account.id });
            return (
              <TaskToggleRow
                key={`custom-${task.id}`}
                name={task.name}
                meta={`${CADENCE_LABEL[task.cadence]} · max ${task.maxCount} · custom`}
                verified
                checked={existing?.enabled ?? false}
                pending={togglePending}
                onToggle={() => toggle({ customTaskId: task.id }, { accountId: account.id }, existing)}
                onDelete={() => setDeletingCustomTask(task)}
              />
            );
          })}
          {accountScopedCatalog.length === 0 && accountScopedCustom.length === 0 && (
            <p className="text-sm text-text-faint">No account-level tasks in the catalog yet.</p>
          )}
        </ul>
      </section>

      {account.characters.map((character) => (
        <section key={character.id} className="rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
          <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-text-faint">
            {character.name}{' '}
            <span className="normal-case tracking-normal text-text-faint">· Lv.{character.level}</span>
          </h2>
          <ul className="space-y-2">
            {characterScopedCatalog.map((task) => {
              const existing = findAttachment(
                attachments,
                { taskDefSlug: task.slug },
                { characterId: character.id },
              );
              const reason = eligibilityReason(task, character);
              return (
                <TaskToggleRow
                  key={task.slug}
                  name={task.name}
                  meta={`${CADENCE_LABEL[task.cadence]} · max ${task.maxCount}`}
                  verified={task.verified}
                  disabledReason={reason}
                  checked={existing?.enabled ?? false}
                  pending={togglePending}
                  onToggle={() =>
                    toggle({ taskDefSlug: task.slug }, { characterId: character.id }, existing)
                  }
                />
              );
            })}
            {characterScopedCustom.map((task) => {
              const existing = findAttachment(
                attachments,
                { customTaskId: task.id },
                { characterId: character.id },
              );
              return (
                <TaskToggleRow
                  key={`custom-${task.id}`}
                  name={task.name}
                  meta={`${CADENCE_LABEL[task.cadence]} · max ${task.maxCount} · custom`}
                  verified
                  checked={existing?.enabled ?? false}
                  pending={togglePending}
                  onToggle={() =>
                    toggle({ customTaskId: task.id }, { characterId: character.id }, existing)
                  }
                  onDelete={() => setDeletingCustomTask(task)}
                />
              );
            })}
            {characterScopedCatalog.length === 0 && characterScopedCustom.length === 0 && (
              <p className="text-sm text-text-faint">No character-level tasks in the catalog yet.</p>
            )}
          </ul>
        </section>
      ))}

      <button
        type="button"
        onClick={() => setCreatingCustomTask(true)}
        className="min-h-tap w-full rounded-lg border border-dashed border-border-strong text-sm font-semibold text-text-faint transition-colors hover:border-accent hover:text-accent"
      >
        + New custom task
      </button>

      {creatingCustomTask && <CustomTaskFormModal onClose={() => setCreatingCustomTask(false)} />}

      {deletingCustomTask && (
        <ConfirmDialog
          title="Delete custom task"
          message={`Deleting "${deletingCustomTask.name}" removes it from every character/account tracking it, along with any recorded completions. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          pending={deleteCustomTask.isPending}
          onCancel={() => setDeletingCustomTask(null)}
          onConfirm={() =>
            deleteCustomTask.mutate(deletingCustomTask.id, { onSuccess: () => setDeletingCustomTask(null) })
          }
        />
      )}
    </div>
  );
}
